import React, { useCallback, useEffect, useMemo } from "react";
import { usePatient } from "../state/patientContext";
import { PatientColorMap } from "../layout/patientColorMap";
import { ActionButton } from "./uiControllers/CommonStyled";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { usePatientRouteChanger } from "../PatientRoutes/routeChanger";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";
import { useTranslate } from "../hooks/useTranslate";
import { RouteContainer } from "../PatientRoutes/RouteContainer";
import styled from "@mui/material/styles/styled";
import { usePatientSummaryByContext } from "fe-common/patientSummary/useFetchPatientSummary";
import {
  PatientRoute,
  PatientRoute_Default,
} from "../PatientRoutes/PatientRoute";
import { useWidgetSettings } from "../widget/useWidgetSettings";
import { useHistory } from "react-router";
import PatientSummaryAgeAndGender from "./uiControllers/patientSummaryAgeAndGender";
import { usePatientFeatureFlags } from "../hooks/usePatientFeatureFlags";
import { useClinicData } from "../clinic/clinicHooks";
import { useQueryParams } from "fe-common/useQueryParams";
import { useConversationDriver } from "../driver/ConversationDriverProvider";
import { WaveArea, WaveAreaAlignment } from "../style/waveStyles";
import { scrollableStyle } from "fe-common/ui/commonStyles";
import {
  onRtl,
  paddingLeftToPaddingRightOnRtl,
} from "fe-web-components/rtl/rtlStyles";
import { ThankYouAnimation } from "./animation/thankYouAnimation";
import { CommonBackButton } from "./uiControllers/commonBackButton";
import { useShowReviewPopup } from "../layout/useShowReviewPopup";
import clsx from "clsx";

//region [[ Styles ]]
const Container = styled("div")({
  backgroundColor: "white",
  position: "relative",
  minHeight: "100%",
  display: "flex",
  flexFlow: "column",
  justifyContent: "space-between",
  maxHeight: "100vh",
  overflow: "hidden",
});

const Summary = styled("div")({
  padding: "0 3%",
  "&.locale-changer": {
    paddingTop: "44px",
  },
  // Make it grow trough the other to make the text align to top
  flex: 2,
  backgroundColor: "white",
  ...scrollableStyle,
});

const Section = styled("div")({
  display: "flex",
  flexFlow: "column",
  "& p.text": {
    color: PatientColorMap.Default,
  },
  "& span.title": {
    color: PatientColorMap.Primary,
  },
  marginBottom: "16px",
});

const SectionTitle = styled("span")({
  fontWeight: 700,
  fontSize: "14px",
  marginBottom: "8px",
  color: PatientColorMap.Primary,
});

const ChiefComplaintContainer = styled("div")({
  fontWeight: 400,
  margin: "20px 0",
  fontSize: "14px",
  color: PatientColorMap.Default,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "flex-start",
  textAlign: "left",
  ...onRtl({
    textAlign: "right",
  }),
});

const ChiefComplaint = styled("div")({
  ...paddingLeftToPaddingRightOnRtl(6),
  fontSize: "14px",
  fontWeight: 700,
});

const SummaryFooter = styled(WaveArea)({
  paddingTop: "20px",
  "& svg path": {
    fill: PatientColorMap.PrimaryLight,
  },
  "& .content": {
    justifyContent: "center",
  },
});

const Text = styled("div")({
  lineHeight: "20px",
  fontSize: "15px",
  fontWeight: 500,
  whiteSpace: "break-spaces",
  color: PatientColorMap.Default,
});

const SummaryHeader = styled("div")({
  fontSize: "19px",
  marginTop: "3%",
});

const SummaryBox = styled("div")({
  marginTop: "20px",
  border: "1px solid #EBEBEB",
  boxShadow: "0px 7px 17px rgba(0, 0, 0, 0.05)",
  borderRadius: "8px",
  padding: "20px",
  paddingTop: 0,
  marginBottom: "41%",
});

const SummaryBoxTitle = styled("div")({
  background: PatientColorMap.Primary,
  padding: "17px 12px",
  fontWeight: 700,
  fontSize: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: PatientColorMap.White,
  margin: "10px -20px",
  marginTop: 0,
  textAlign: "center",
  borderTopRightRadius: "8px",
  borderTopLeftRadius: "8px",
});

const ThanksTitleContainer = styled("div")({
  display: "flex",
  flexFlow: "row",
  justifyItems: "stretch",
});

const ThanksButtonAndTextContainer = styled("div")({
  display: "flex",
  flexFlow: "column",
  alignItems: "flex-start",
  flex: 1,
});

const BackButtonContainer = styled("div")({
  display: "flex",
  flexFlow: "row",
  maxWidth: "50px",
});

const ThankYouTextContainer = styled("div")({
  display: "flex",
  flexFlow: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  flex: 1,
});

const ThanksTitle = styled("span")({
  fontSize: "20px",
  fontWeight: 700,
  color: PatientColorMap.Primary,
});

const ThanksSubTitle = styled("div")({
  fontSize: "13px",
  fontWeight: 500,
});

const AppName = styled("span")({
  color: PatientColorMap.Primary,
});
//endregion [[ Styles ]]

//region [[ Props ]]
interface SummaryContainerProps extends RouteContainer {}
//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

//region [[ Texts ]]
export const CHIEF_COMPLAINT = "CHIEF_COMPLAINT";
export const SHOW_ME_THE_RESULTS_TEXT = "DISCLAIMER_BUTTON";
//endregion [[ Texts ]]

export const Patient1stSummary = ({
  embedded,
  ...props
}: SummaryContainerProps) => {
  const { partnerId } = useWidgetSettings();

  const goBack = useQueryParams().get("backto");

  const { clinicSettings } = useClinicData();

  const linkSummaryToResults = clinicSettings.linkSummaryToResults;

  const supportedSections = useMemo(
    () => clinicSettings.patientSummarySections,
    [clinicSettings],
  );

  const { name: clinicName, clinic: clinicId } = useClinicData();

  useEffect(() => {
    evt({
      category: CATEGORY.PATIENT_RESULT,
      action: ACTION.PATIENT_PAGE_LOAD,
      label: "Summary page",
      partnerId,
      props: {
        clinicName,
        clinicId,
      },
    });
  }, [partnerId, clinicName, clinicId]);

  const showNoOnNegates = usePatientFeatureFlags("patientSummaryNoOnNegates");
  const showAssessment = usePatientFeatureFlags("patientSummaryAssessment");
  const isLocaleChangerFlagActive = usePatientFeatureFlags("localeChanger");

  const routeChanger = usePatientRouteChanger();

  const { translate } = useTranslate();

  const history = useHistory();

  const { shareId, caseId, gender, agreeOnTerms } = usePatient();

  // Show review if it wasn't dismissed before
  useShowReviewPopup();

  const currentContext = useConversationDriver()?.getCurrentContext();

  const discardNoForNegate = !showNoOnNegates;

  const { summary, loading } = usePatientSummaryByContext(currentContext, {
    discardNoForNegate,
    showAssessment: !!showAssessment,
  });

  const showMeResults = () => {
    history.push("/" + PatientRoute.Disclaimer);
  };

  const enabledSections = useMemo(
    () =>
      summary?.sections?.filter(
        (s) =>
          !supportedSections?.length ||
          supportedSections?.find((supported) => supported === s.type),
      ),
    [supportedSections, summary],
  );

  const handleBackClicked = useCallback(() => {
    routeChanger(goBack);
  }, [routeChanger, goBack]);

  if (
    !summary?.main &&
    !loading &&
    !shareId &&
    !caseId &&
    !embedded &&
    !agreeOnTerms
  ) {
    routeChanger(PatientRoute_Default);
    return null;
  }

  if (loading || (!summary?.main && shareId)) {
    // Wait for the data to come
    return <ThreeDotsLoader color={PatientColorMap.Primary} />;
  }

  return (
    <Container className={props.className}>
      <Summary
        className={clsx(
          "summary",
          isLocaleChangerFlagActive ? "locale-changer" : "",
        )}
      >
        <SummaryHeader className={"summary-header"}>
          <ThanksTitleContainer className={"thanks-title-container"}>
            <ThanksButtonAndTextContainer
              className={"thanks-button-and-text-container"}
            >
              {goBack && (
                <BackButtonContainer className={"back-button-container"}>
                  <CommonBackButton
                    className={"back-button"}
                    onClick={handleBackClicked}
                  />
                </BackButtonContainer>
              )}
              <ThankYouTextContainer className={"thank-you-text-container"}>
                <ThanksTitle>{translate("THANKS_TITLE")}</ThanksTitle>
                <ThanksSubTitle className={"thanks-sub-title"}>
                  {translate("SUMMARY_TEXT")
                    .split("#APP_NAME#")
                    .map((t, index, arr) =>
                      index < arr.length - 1
                        ? [t, <AppName>{translate("APP_NAME")}</AppName>]
                        : [t],
                    )}
                </ThanksSubTitle>
              </ThankYouTextContainer>
            </ThanksButtonAndTextContainer>
            <ThankYouAnimation size={160} />
          </ThanksTitleContainer>
        </SummaryHeader>
        <SummaryBox className={"summary-box"}>
          <SummaryBoxTitle className={"summary-box-title"}>
            {translate("SUMMARY_HEADER_TEXT")}
          </SummaryBoxTitle>
          {!!summary?.age && gender && (
            <PatientSummaryAgeAndGender
              age={summary?.age?.toString()}
              gender={gender}
            />
          )}
          {summary?.main && (
            <ChiefComplaintContainer className={"main-complaint-container"}>
              {translate(CHIEF_COMPLAINT)}:{" "}
              {summary?.main && (
                <ChiefComplaint>{summary?.main}</ChiefComplaint>
              )}
            </ChiefComplaintContainer>
          )}
          {enabledSections.map((section, index) => (
            <Section key={`section-${section.title || index}`}>
              <SectionTitle className={"title"}>{section.title}</SectionTitle>
              <Text className={"text"}>{section.content}</Text>
            </Section>
          ))}
        </SummaryBox>
      </Summary>
      {!goBack && linkSummaryToResults && (
        <SummaryFooter
          alignment={WaveAreaAlignment.Bottom}
          color={PatientColorMap.PrimaryLight}
        >
          <ActionButton
            label={translate(SHOW_ME_THE_RESULTS_TEXT)}
            onClick={showMeResults}
          />
        </SummaryFooter>
      )}
    </Container>
  );
};
